import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import classNames from 'classnames';

// MUI components & icons
import Button from '@mui/material/Button';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import styles from './ExpandableFade.module.css';
import FlatButton from "../FlatButton/FlatButton";
import NobiEventTracker from "../../utils/NobiEventTracker";
import Events from "../../utils/Events";

/**
 * Generic ExpandableFade wrapper. Clips its children at a `collapsedHeight`, fades the bottom `fadeHeight`, and toggles expand/collapse with an MUI Button.
 *
 * @param {string|number} collapsedHeight - Required: container height when collapsed (px or any CSS unit)
 * @param {string|number} fadeHeight - Required: height of the fade overlay (px or any CSS unit)
 * @param {ReactNode} children - Content to show/hide
 * @param {boolean} showButton - Show or hide the toggle button
 * @param {string} expandLabel - Text when collapsed
 * @param {string} collapseLabel - Text when expanded
 * @param {string} className - Extra class on root node
 * @param {string} buttonClassName - Extra class on the button
 * @param {object} brandTheme - Theme object for styling
 * @param {string} conversationId - Conversation ID for event tracking
 * @param {string} entryPoint - Entry point for event tracking
 * @param {string} merchant - The merchant object for event tracking
 * @param {object} transition - Framer Motion transition override
 *
 */
export default function ExpandableFade({
    collapsedHeight,
    fadeHeight,
    children,
    showButton = true,
    expandLabel = 'Show More',
    collapseLabel = 'Show Less',
    className,
    buttonClassName,
    brandTheme,
    conversationId,
    entryPoint,
    merchant,
    transition = { duration: 0.4, ease: 'easeInOut' }
}) {
    const [expanded, setExpanded] = useState(false);
    const [fullHeight, setFullHeight] = useState(0);

    // Normalize numeric values to "NNpx"
    const animateDefaultHeight = typeof collapsedHeight === 'number'
        ? `${collapsedHeight}px`
        : collapsedHeight;
    const expandedHeight = typeof fadeHeight === 'number'
        ? `${fadeHeight}px`
        : fadeHeight;

    const contentRef = useRef(null);

    // 2) after each render (and when children change), record scrollHeight
    useEffect(() => {
        if (contentRef.current) {
            setFullHeight(contentRef.current.scrollHeight);
        }
    }, [children]);

    function toggleExpansion() {
        let eventDetails = {
            conversationId: conversationId
        };

        let eventName = Events.ClickExpandProductCollectionButton;
        if (expanded) {
            eventName = Events.ClickCollapseProductCollectionButton;
        }

        NobiEventTracker.trackEvent(
            merchant.id,
            entryPoint,
            eventName,
            false,
            null,
            null,
            eventDetails);

        setExpanded(!expanded);
    }

    return (
        <div className={classNames(styles.root, className)}>
            {/* animated height wrapper */}
            <motion.div
                ref={contentRef}
                initial={{ maxHeight: animateDefaultHeight }}
                animate={{
                    maxHeight: expanded
                        ? `${fullHeight}px`
                        : animateDefaultHeight
                }}
                transition={transition}
                style={{ overflow: 'hidden' }}
                className={styles.wrapper}>
                {children}
            </motion.div>

            {/* fade overlay (only when collapsed) */}
            {!expanded && (
                <div
                    className={styles.fadeOverlay}
                    style={{ height: expandedHeight }}
                />
            )}

            {/* toggle button */}
            {showButton && (
                <FlatButton
                    variant="contained"
                    disableElevation
                    onClick={toggleExpansion}
                    className={`${styles.expandButton} ${buttonClassName || ''} ${expanded ? styles.showLessButton : ''}`}
                    endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    aria-expanded={expanded}
                    sx={{
                        backgroundColor: brandTheme?.general?.buttons?.backgroundColor,
                        color: brandTheme?.general?.buttons?.textColor,
                        '&:hover': {
                            backgroundColor: brandTheme?.general?.buttons?.hoverColor,
                        },
                        fontFamily: brandTheme?.general?.buttons?.fontFamily || "inherit",
                    }}
                >
                    {expanded ? collapseLabel : expandLabel}
                </FlatButton>
            )}
        </div>
    );
}

ExpandableFade.propTypes = {
    collapsedHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    fadeHeight:      PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    children:        PropTypes.node.isRequired,
    showButton:      PropTypes.bool,
    expandLabel:     PropTypes.string,
    collapseLabel:   PropTypes.string,
    className:       PropTypes.string,
    buttonClassName: PropTypes.string,
    brandTheme:      PropTypes.object,
    conversationId:  PropTypes.string,
    entryPoint:      PropTypes.string,
    merchant:      PropTypes.object,
    transition:      PropTypes.object,
};
